import { computeSceneMotion } from "@/lib/timeline/scene/SceneMotion/motion";
import {
  characterGroupIdByCharacter,
  characterGroupsAtPlayhead,
} from "../../character/ActiveCharacterGroup/atPlayhead";
import {
  MARKER_DIAMETER,
  markerDiscAxisOffsetY,
  timelineRailAxisY,
} from "../../marker/MarkerMetrics/metrics";
import { markerRailPose } from "../../rail/RailPose/pose";
import type { BoardSample, ChapterBoard } from "../ChapterBoard/types";
import { HIDDEN_POSE, sampleMarkerTrack } from "../MarkerTrack/track";

export function sampleBoard(
  board: ChapterBoard,
  playheadMs: number,
): BoardSample {
  const motion = computeSceneMotion(
    playheadMs,
    board.referenceViewportWidth,
    board.totalDurationMs,
  );
  const groupByCharacter = characterGroupIdByCharacter(
    characterGroupsAtPlayhead(board.rail, playheadMs),
  );
  const axisY = timelineRailAxisY();
  const discRadius = MARKER_DIAMETER / 2;
  const axisOffset = markerDiscAxisOffsetY();
  const characters = board.characterMarkers.map((marker) => {
    const pose = sampleMarkerTrack(marker.track, playheadMs);
    const railPose = markerRailPose(marker.characterId, playheadMs, board.rail);
    const groupId = groupByCharacter.get(marker.characterId) ?? marker.characterId;
    // A stay that has ended must not keep its last tucked frame. The track
    // holds that pose and fades it out later, which leaves the disc behind
    // while the host keeps moving.
    if (railPose === null) {
      return {
        characterId: marker.characterId,
        groupId,
        pose: HIDDEN_POSE,
      };
    }
    const centerX = motion.groupCenterX + railPose.dx;
    const centerY = axisY + railPose.railDy + railPose.dy;
    return {
      characterId: marker.characterId,
      groupId,
      ...(railPose.parked === true ? { parked: true } : {}),
      pose: {
        ...pose,
        x: centerX,
        y: centerY - discRadius - axisOffset,
        scale: railPose.scale,
        behind: railPose.behind,
        nameOpacity: railPose.nameOpacity * (railPose.fade ?? 1),
        fade: railPose.fade ?? 1,
        visible: true,
        opacity: railPose.parked === true ? 1 : pose.opacity,
      },
    };
  });
  const locationGroups = board.locationGroups.map((group) => ({
    branchId: group.branchId,
    siteLabel: group.site.siteLabel,
    sitePose: sampleMarkerTrack(group.site.track, playheadMs),
    parts: group.parts.map((part) => ({
      placeId: part.placeId,
      partLabel: part.partLabel,
      pose: sampleMarkerTrack(part.track, playheadMs),
    })),
  }));
  return {
    playheadMs,
    motion,
    characters,
    locationGroups,
  };
}
